import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  CircularProgress,
  Chip,
} from "@mui/material";
import Pagelayout from "../../components/Pagelayout";
import { useNavigate } from "react-router-dom";
import { HEADERS } from "../../../../../API/UrlAndPaths";
import Swal from "sweetalert2";

const LeaveBalance = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balanceReport, setBalanceReport] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [employeeImages, setEmployeeImages] = useState({});
  
  // Allocation dialog state
  const [allocateDialogOpen, setAllocateDialogOpen] = useState(false);
  const [bulkAllocateDialogOpen, setBulkAllocateDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [allocatedDays, setAllocatedDays] = useState(0);
  const [carriedForward, setCarriedForward] = useState(0);

  const handleBackBtn = () => {
    navigate("/userdashboard/payroll/attendance/payrollLeave");
  };

  // Memoized image component to prevent re-renders (same as PayrollLeave)
  const EmployeeImage = useMemo(() => {
    // Inline SVG placeholder (40x40 circle) eliminates network 404 loops
    const FALLBACK_DATA_URL =
      'data:image/svg+xml;utf8,' +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
          <rect width="40" height="40" fill="#f0f0f0"/>
          <circle cx="20" cy="15" r="8" fill="#c0c0c0"/>
          <rect x="10" y="26" width="20" height="8" rx="4" fill="#d0d0d0"/>
        </svg>`
      );

    return ({ src, alt }) => (
      <img
        src={src || FALLBACK_DATA_URL}
        alt={alt}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          objectFit: 'cover',
          border: '1px solid #e0e0e0'
        }}
        onError={(e) => {
          // Prevent infinite onError loop by removing handler, then set fallback
          e.currentTarget.onerror = null;
          e.currentTarget.src = FALLBACK_DATA_URL;
        }}
      />
    );
  }, []);

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      const res = await axios.get("employee/getAllEmployees", HEADERS);
      console.log("LeaveBalance - Employees Response:", res);
      if (res.status === 200) {
        const employeeData = res.data.data || res.data || [];
        console.log("LeaveBalance - Setting employees:", employeeData);
        setEmployees(employeeData);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  // Fetch leave types
  const fetchLeaveTypes = async () => {
    try {
      const res = await axios.get("employee/viewLeaveType", HEADERS);
      console.log("LeaveBalance - Leave Types Response:", res);
      if (res.status === 200) {
        const leaveTypeData = res.data.data || res.data || [];
        console.log("LeaveBalance - Setting leave types:", leaveTypeData);
        setLeaveTypes(leaveTypeData);
      }
    } catch (error) {
      console.error("Error fetching leave types:", error);
    }
  };

  // Fetch balance report
  const fetchBalanceReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `payroll/leave/balance/report?year=${selectedYear}`,
        HEADERS
      );
      if (res.status === 200) {
        const employees = res.data.employees || [];
        
        // Fetch images for each employee
        const employeesWithImages = await Promise.all(employees.map(async (employee) => {
          const imageUrl = await fetchEmployeeImage(employee.employeeId);
          return {
            ...employee,
            employeeImage: imageUrl
          };
        }));
        
        setBalanceReport(employeesWithImages);
      }
    } catch (error) {
      console.error("Error fetching balance report:", error);
      Swal.fire("Error", "Failed to fetch leave balance report", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchLeaveTypes();
  }, []);

  useEffect(() => {
    fetchBalanceReport();
  }, [selectedYear]);

  // Fetch employee image by ID
  const fetchEmployeeImage = async (employeeId) => {
    if (!employeeId || employeeImages[employeeId]) return employeeImages[employeeId];
    
    try {
      console.log('LeaveBalance - Fetching employee image for ID:', employeeId);
      const response = await axios.post(`employee/getEmployeeById`, { id: employeeId }, HEADERS);
      const employee = response.data?.data || response.data;
      if (employee) {
        const imageUrl = 
          employee.imageUrl ||
          employee.profileImage ||
          employee.image ||
          employee.employeePhoto ||
          employee.empImage ||
          null;
        
        if (imageUrl) {
          // Build full URL if needed
          let fullImageUrl = imageUrl;
          if (!imageUrl.startsWith('http')) {
            const base = (axios.defaults.baseURL || '').replace(/\\\\/g, '/').replace(/\/$/, '');
            const path = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
            fullImageUrl = `${base}${path}`;
          }
          
          setEmployeeImages(prev => ({ ...prev, [employeeId]: fullImageUrl }));
          return fullImageUrl;
        }
      }
    } catch (error) {
      console.warn('LeaveBalance - Failed to fetch employee image for ID:', employeeId, error);
    }
    return null;
  };

  // Handle single employee allocation
  const handleAllocate = async () => {
    if (!selectedEmployee || !selectedLeaveType || allocatedDays <= 0) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    try {
      const res = await axios.post(
        "payroll/leave/allocate",
        {
          employeeId: selectedEmployee._id,
          leaveTypeId: selectedLeaveType._id,
          year: selectedYear,
          allocated: Number(allocatedDays),
          carriedForward: Number(carriedForward),
        },
        HEADERS
      );

      if (res.status === 200) {
        Swal.fire("Success", "Leaves allocated successfully", "success");
        setAllocateDialogOpen(false);
        resetAllocationForm();
        fetchBalanceReport();
      }
    } catch (error) {
      console.error("Error allocating leaves:", error);
      Swal.fire(
        "Error",
        error.response?.data?.msg || "Failed to allocate leaves",
        "error"
      );
    }
  };

  // Handle bulk allocation
  const handleBulkAllocate = async () => {
    if (!selectedLeaveType || allocatedDays <= 0) {
      Swal.fire("Error", "Please select leave type and enter days", "error");
      return;
    }

    const result = await Swal.fire({
      title: "Bulk Allocate Leaves?",
      text: `Allocate ${allocatedDays} days of ${selectedLeaveType.leaveType} to ALL employees?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, allocate!",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await axios.post(
        "payroll/leave/allocate-bulk",
        {
          leaveTypeId: selectedLeaveType._id,
          year: selectedYear,
          allocated: Number(allocatedDays),
        },
        HEADERS
      );

      if (res.status === 200) {
        Swal.fire(
          "Success",
          `Allocated to ${res.data.successCount} employees`,
          "success"
        );
        setBulkAllocateDialogOpen(false);
        resetAllocationForm();
        fetchBalanceReport();
      }
    } catch (error) {
      console.error("Error bulk allocating leaves:", error);
      Swal.fire(
        "Error",
        error.response?.data?.msg || "Failed to allocate leaves",
        "error"
      );
    }
  };

  const resetAllocationForm = () => {
    setSelectedEmployee(null);
    setSelectedLeaveType(null);
    setAllocatedDays(0);
    setCarriedForward(0);
  };

  return (
    <Pagelayout 
      pageName="Leave Balance Management" 
      headerLayout={2} 
      actionBtn={false}
      handleBack={handleBackBtn}
    >
      <Box sx={{ p: 3 }}>
        {/* Header Actions */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => setAllocateDialogOpen(true)}
            >
              Allocate to Employee
            </Button>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={() => setBulkAllocateDialogOpen(true)}
            >
              Bulk Allocate
            </Button>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Button
              fullWidth
              variant="contained"
              color="info"
              onClick={() => navigate('/userdashboard/payroll/attendance/LeaveType')}
            >
              Manage Leave Types
            </Button>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={fetchBalanceReport}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>

        {/* Balance Report Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell><strong>Image</strong></TableCell>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Emp ID</strong></TableCell>
                  <TableCell><strong>Leave Type</strong></TableCell>
                  <TableCell align="center"><strong>Allocated</strong></TableCell>
                  <TableCell align="center"><strong>Used</strong></TableCell>
                  <TableCell align="center"><strong>Pending</strong></TableCell>
                  <TableCell align="center"><strong>Available</strong></TableCell>
                  <TableCell align="center"><strong>Carried Forward</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balanceReport.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No leave balances found. Please allocate leaves first.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  balanceReport.map((employee) =>
                    employee.balances.map((balance, index) => (
                      <TableRow key={`${employee.employeeId}-${index}`}>
                        {index === 0 && (
                          <>
                            <TableCell rowSpan={employee.balances.length}>
                              <EmployeeImage 
                                src={employee.employeeImage} 
                                alt={employee.employeeName || 'Employee'} 
                              />
                            </TableCell>
                            <TableCell rowSpan={employee.balances.length}>
                              {employee.employeeName}
                            </TableCell>
                            <TableCell rowSpan={employee.balances.length}>
                              {employee.employeeCode}
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          <Chip
                            label={balance.leaveType}
                            size="small"
                            color={balance.isPaid ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell align="center">{balance.allocated}</TableCell>
                        <TableCell align="center">{balance.used}</TableCell>
                        <TableCell align="center">
                          <Chip label={balance.pending} size="small" color="warning" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={balance.available}
                            size="small"
                            color={balance.available > 0 ? "primary" : "error"}
                          />
                        </TableCell>
                        <TableCell align="center">{balance.carriedForward}</TableCell>
                      </TableRow>
                    ))
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Single Employee Allocation Dialog */}
        <Dialog
          open={allocateDialogOpen}
          onClose={() => setAllocateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Allocate Leaves to Employee</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={employees}
                    getOptionLabel={(option) =>
                      `${option.staff_name} (EMP${option.emp_id})`
                    }
                    value={selectedEmployee}
                    onChange={(e, newValue) => setSelectedEmployee(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Employee" required />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={leaveTypes}
                    getOptionLabel={(option) =>
                      `${option.leaveType} (${option.code || ""})`
                    }
                    value={selectedLeaveType}
                    onChange={(e, newValue) => setSelectedLeaveType(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Leave Type" required />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Year"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Allocated Days"
                    value={allocatedDays}
                    onChange={(e) => setAllocatedDays(Number(e.target.value))}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Carried Forward"
                    value={carriedForward}
                    onChange={(e) => setCarriedForward(Number(e.target.value))}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAllocateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAllocate} variant="contained" color="primary">
              Allocate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Allocation Dialog */}
        <Dialog
          open={bulkAllocateDialogOpen}
          onClose={() => setBulkAllocateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Bulk Allocate Leaves to All Employees</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={leaveTypes}
                    getOptionLabel={(option) =>
                      `${option.leaveType} (${option.code || ""})`
                    }
                    value={selectedLeaveType}
                    onChange={(e, newValue) => setSelectedLeaveType(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Leave Type" required />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Year"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Allocated Days"
                    value={allocatedDays}
                    onChange={(e) => setAllocatedDays(Number(e.target.value))}
                    required
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkAllocateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkAllocate}
              variant="contained"
              color="secondary"
            >
              Allocate to All
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Pagelayout>
  );
};

export default LeaveBalance;
